const m = require('minthril');
const { component } = require('../../component');
const { Breadcrumbs } = require('../../components/breadcrumbs');
const { CompatibilityLogos } = require('../../components/compatibility-logos');

const exampleScreenshot = 'https://spache.online/api/sites/ibE3BaPOmqFB3UTrrLpR/routes/8a5edab282632443219e051e4ade2d1d5bbc671c781051bf1437897cbdfea0f1/screenshot.jpg';

function flowIllustration(number, title, content, modifier = '') {
  return m('figure.how-it-works__visual', [
    m('div.how-it-works__visual-frame', { class: modifier }, [
      m('div.how-it-works__visual-topbar', [
        m('i'), m('i'), m('i'),
        m('span', `SPACHE · ${number}`)
      ]),
      content
    ]),
    m('figcaption', title)
  ]);
}

const HowSpacheWorksPage = component(() => ({
  view: () => m('article.panel.docs-page.how-it-works', [
    m(Breadcrumbs, {
      items: [
        { label: 'Docs', href: '/docs' },
        { label: 'How Spache works' }
      ]
    }),
    m('header.how-it-works__intro', [
      m('p.how-it-works__eyebrow', 'A tour of a real Spache site. This one!'),
      m('h2', 'From an empty app shell to a page ready to read'),
      m('p',
        'Spache.online is itself a single-page app. Its origin does not send a finished page for every URL: ' +
        'it sends an app shell and JavaScript, and the browser builds the page. Here is how Spache turns that ' +
        'into rendered HTML that can be served straight away.'
      )
    ]),
    m('div.how-it-works__story', [
      m('section.how-it-works__step', [
        m('div.how-it-works__copy', [
          m('span.how-it-works__step-number', '01 / The starting point'),
          m('h3', 'The origin is a JavaScript app, not a finished page'),
          m('p',
            'Ask the origin for a route and you get the small document that starts the app. The meaningful page ' +
            'content is created after JavaScript runs in a browser. A visitor can do that; a crawler that only ' +
            'reads the initial response may have little or nothing useful to index.'
          ),
          m('p',
            'That is exactly the kind of site Spache.online is. We use our own site as the example because this ' +
            'page is being delivered through Spache too.'
          )
        ]),
        flowIllustration('01', 'The origin provides the app shell',
          m('div.how-it-works__shell', [
            m('span', 'https://spache.online/'),
            m('pre', '<div id="app"></div>\n<script src="/index.js"></script>'),
            m('small', 'The browser still has to run the app to build the page.')
          ]), 'how-it-works__visual-frame--shell')
      ]),
      m('section.how-it-works__step', [
        m('div.how-it-works__copy', [
          m('span.how-it-works__step-number', '02 / Three ways to send HTML'),
          m('h3', 'The problem is the same; the rendering model is different'),
          m('p', [
            'A browser-only SPA starts with an app shell and builds the page after JavaScript runs. To send useful ' +
            'HTML in the first response, teams usually choose one of a few approaches.'
          ]),
          m('ul', [
            m('li', [
              m('strong', 'Generate static HTML at build time. '),
              'A build renders pages into files that can be served directly from a CDN. This is very fast and ' +
              'works well when routes and content can be prepared ahead of time. The trade-off is keeping every ' +
              'generated route fresh: content changes need a rebuild or revalidation, and large or frequently ' +
              'changing route sets add work to the build and publishing pipeline. Adapting an existing SPA to ' +
              'generate all that HTML can also mean changing how the app is built.'
            ]),
            m('li', [
            m('strong', 'Render on a server. '),
              'A framework such as ',
              m('a', {
                href: 'https://nextjs.org/docs/pages/building-your-application/rendering/server-side-rendering',
                target: '_blank',
                rel: 'noreferrer'
              }, 'Next.js'),
              ' can generate HTML at build time or render a page for a request when it needs current request-time ' +
              'data. It is a capable way to build dynamic pages, but adopting server rendering means learning and ' +
              'maintaining its rendering conventions, data-loading boundaries, caching and revalidation behaviour, ' +
              'and deployment/runtime setup. For a team with an existing SPA, that can turn “make the page ' +
              'available as HTML” into a broader app migration—and pull development time and agent context away ' +
              'from the product itself.'
            ]),
            m('li', [
              m('strong', 'Render the existing SPA outside the app. '),
              'Spache opens the deployed app in a browser, saves its rendered HTML, and serves that through a ' +
              'shared cache. Your SPA and origin stay as they are; Spache takes responsibility for producing ' +
              'and refreshing the rendered copy.'
            ])
          ]),
          m('p',
            'You can take on the rendering model, build pipeline, and runtime yourself—or keep the SPA you already ' +
            'have and let Spache produce and serve its rendered copy. Spend that engineering time and agent context ' +
            'on the product, not on making another rendering stack work.'
          )
        ]),
        flowIllustration('02', 'Where the HTML is produced',
          m('div.how-it-works__approaches', [
            m('div.how-it-works__approach', [
              m('span', 'BUILD'),
              m('b', 'Static HTML'),
              m('small', 'Generated ahead of time; serve files from a CDN')
            ]),
            m('div.how-it-works__approach', [
              m('span', 'REQUEST'),
              m('b', 'Server rendering'),
              m('small', 'App server creates or streams HTML for the request')
            ]),
          m('div.how-it-works__approach.how-it-works__approach--spache', [
              m('span', 'BROWSER + CACHE'),
              m('b', 'Spache'),
              m('small', 'Renders your deployed SPA, then serves the cached HTML')
            ])
          ]), 'how-it-works__visual-frame--approaches')
      ]),
      m('section.how-it-works__step', [
        m('div.how-it-works__copy', [
          m('span.how-it-works__step-number', '03 / Render once'),
          m('h3', 'Spache runs the app and captures the finished page'),
          m('p',
            'A Spache render worker opens the site in a real browser, lets the app start, and records the resulting ' +
            'HTML. If configured, it also captures the API responses the app uses while starting. Spache stores ' +
            'the rendered result so the next request does not need to rebuild that first view.'
          ),
          m('p',
            'This is the live screenshot for Spache.online’s home route, and it always reflects the latest render. ' +
            'It is a visual check of the result; crawlers receive the corresponding HTML, not this screenshot image.'
          )
        ]),
        flowIllustration('03', 'What the rendered page looks like',
          m('img.how-it-works__screenshot', {
            src: exampleScreenshot,
            alt: 'Spache-generated screenshot of the Spache homepage after its single-page app has rendered',
            loading: 'lazy'
          }), 'how-it-works__visual-frame--screenshot')
      ]),
      m('section.how-it-works__step', [
        m('div.how-it-works__copy', [
          m('span.how-it-works__step-number', '04 / A request arrives'),
          m('h3', 'Spache checks for a ready-to-serve render'),
          m('p',
            'When someone requests a connected domain, that request reaches Spache first. Spache identifies the ' +
            'site and route, then looks for a current cached render. On a cache hit, the rendered HTML is returned ' +
            'instead of making the visitor wait while the app constructs the initial page.'
          ),
          m('p',
            'On a cache miss, normal visitors can receive the origin response while Spache renders in the ' +
            'background. Recognised crawlers wait for a render on a miss, so they can receive the rendered content.'
          )
        ]),
        flowIllustration('04', 'The request path',
          m('div.how-it-works__request-flow', [
            m('div.how-it-works__flow-node', [m('b', 'Visitor'), m('small', 'requests a route')]),
            m('span.how-it-works__flow-arrow', '↓'),
            m('div.how-it-works__flow-node.how-it-works__flow-node--spache', [m('b', 'Spache'), m('small', 'finds the cached render')]),
            m('span.how-it-works__flow-arrow', '↓'),
            m('div.how-it-works__flow-node.how-it-works__flow-node--html', [m('b', 'Rendered HTML'), m('small', 'sent in the response')])
          ]), 'how-it-works__visual-frame--flow')
      ]),
      m('section.how-it-works__step', [
        m('div.how-it-works__copy', [
          m('span.how-it-works__step-number', '05 / Useful content first'),
          m('h3', 'The page can appear before the app bundle finishes'),
          m('p',
            'The response already contains the page content. The browser can parse and display it while it downloads ' +
            'the app bundle and other resources. The app still starts normally in the browser and takes over; Spache ' +
            'does not remove your frontend or turn it into a different framework.'
          ),
          m('p',
            'That means visitors do not have to wait for the app’s startup network requests just to see the initial ' +
            'page. The content is already there, ready to paint.'
          )
        ]),
        flowIllustration('05', 'Content arrives with the document',
          m('div.how-it-works__page-preview', [
            m('div.how-it-works__page-preview-bar'),
            m('div.how-it-works__page-preview-title'),
            m('div.how-it-works__page-preview-line'),
            m('div.how-it-works__page-preview-line how-it-works__page-preview-line--short'),
            m('div.how-it-works__page-preview-card', [
              m('div.how-it-works__page-preview-image'),
              m('div', [m('i'), m('i'), m('i')])
            ]),
            m('span.how-it-works__ready-label', 'Readable content — before app startup completes')
          ]), 'how-it-works__visual-frame--page')
      ]),
      m('section.how-it-works__step', [
        m('div.how-it-works__copy', [
          m('span.how-it-works__step-number', '06 / App data, ready too'),
          m('h3', 'Captured API responses can skip the startup round trip'),
          m('p',
            'For configured API calls made during rendering, Spache can store the response and include replay code ' +
            'with the cached page. When the app makes a matching request as it starts, Spache can return that ' +
            'captured response locally instead of waiting for the origin API again. For POST requests, the request ' +
            'body is part of the match, so distinct queries are kept distinct.'
          ),
          m('p',
            'It is not a promise that every request is eliminated: uncaptured calls, personalised data, and later ' +
            'user actions still use the network as usual. It means the initial page and its captured startup data ' +
            'can be ready together.'
          )
        ]),
        flowIllustration('06', 'Initial data is replayed from the page',
          m('div.how-it-works__replay-flow', [
            m('div.how-it-works__replay-request', 'App asks for startup data'),
            m('span.how-it-works__flow-arrow', '↓'),
            m('div.how-it-works__replay-cache', [m('b', 'Captured response'), m('small', 'matched by URL, method and body')]),
            m('span.how-it-works__flow-arrow', '↓'),
            m('div.how-it-works__replay-ready', 'App continues without waiting on origin')
          ]), 'how-it-works__visual-frame--replay')
      ]),
      m('section.how-it-works__finish', [
        m('h3', 'Your app stays yours. The first view is ready sooner.'),
        m('p',
          'Spache handles browser rendering and shared page delivery around the app you already have. Your team ' +
          'keeps building the product; visitors and crawlers get meaningful HTML without your application needing ' +
          'its own server-rendering stack.'
        ),
        m('a.link-button', { href: '/sites' }, 'Try your site in Spache →')
      ])
    ]),
    m(CompatibilityLogos, {
      description: 'Keep the frontend you have. Spache works with apps that run in a browser.'
    })
  ])
}));

module.exports = { HowSpacheWorksPage };
